import { ChangeDetectorRef, Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { SecurityService } from '@rsm/shared/security';
import { interval, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { ProfilePhotoStoreService } from 'src/app/core/profile-photo/profile-photo-store.service';
import { AgenteClientService } from '@rsm/client/agente-client';

@Component({
  selector: 'rsm-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss']
})
export class SidenavComponent implements OnInit, OnDestroy {
  @Input() logoUrl: string | null = 'assets/logo-rsm-white.svg';
  @Input() userName: string = 'Usuário';
  isSmallScreen = false;
  profilePhoto: string | null = null;
  private readonly managementFocusMaxWidth = 1450;

  get firstUserName(): string {
    return this.userName ? this.userName.substring(0, 1).toUpperCase() : 'U';
  }

  isExpanded: boolean = false;
  showBadges: boolean = false;
  isManagementOpen: boolean = false;
  points = 1250;
  profileCompletionPercent = 100;
  notificationsCount = 0;
  waterAgents = 12;
  fireAgents = 8;

  private profilePhotoSubscription?: Subscription;
  private unreadPollSub?: Subscription;
  private notificationReadSub?: Subscription;

  constructor(
    public router: Router,
    private securityService: SecurityService,
    private profilePhotoStoreService: ProfilePhotoStoreService,
    private changeDetectorRef: ChangeDetectorRef,
    private agentService: AgenteClientService
  ) { }

  ngOnInit() {
    this.profilePhoto = this.profilePhotoStoreService.getPhoto();
    this.profilePhotoSubscription = this.profilePhotoStoreService.photo$.subscribe((photo) => {
      this.profilePhoto = photo;
      this.changeDetectorRef.detectChanges();
    });
    this.profilePhotoStoreService.loadApprovedPhoto();
    this.updateScreenFlags(window.innerWidth);
    this.loadUnreadCount();
    this.unreadPollSub = interval(30_000).pipe(
      switchMap(() => this.agentService.getAdminUnreadCount({ silent: true }))
    ).subscribe(res => {
      this.notificationsCount = res.count;
      this.changeDetectorRef.detectChanges();
    });

    this.notificationReadSub = this.agentService.notificationRead$.pipe(
      switchMap(() => this.agentService.getAdminUnreadCount({ silent: true }))
    ).subscribe(res => {
      this.notificationsCount = res.count;
      this.changeDetectorRef.detectChanges();
    });
  }

  ngOnDestroy() {
    this.profilePhotoSubscription?.unsubscribe();
    this.unreadPollSub?.unsubscribe();
    this.notificationReadSub?.unsubscribe();
  }

  private loadUnreadCount(): void {
    // Badge de notificações: atualiza sozinho, sem overlay de carregamento.
    this.agentService.getAdminUnreadCount({ silent: true }).subscribe(res => {
      this.notificationsCount = res.count;
      this.changeDetectorRef.detectChanges();
    });
  }

  toggleMenu() {
    this.isExpanded = !this.isExpanded;
  }

  onMainMenuClick(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    if (target.closest('.logoBox')) {
      return;
    }

    if (
      target.closest('.navLink') ||
      target.closest('.managementSubmenu') ||
      target.closest('.avatar-dot') ||
      target.closest('.badges-wrapper') ||
      target.closest('.badge-item')
    ) {
      return;
    }

    this.toggleMenu();
  }

  private updateScreenFlags(viewportWidth: number) {
    this.isSmallScreen = viewportWidth <= this.managementFocusMaxWidth;
    this.isExpanded = !this.isSmallScreen;
  }

  toggleManagement(event: Event) {
    event.stopPropagation();
    this.isManagementOpen = !this.isManagementOpen;
  }

  toggleBadges(event: Event) {
    event.stopPropagation();
    this.showBadges = !this.showBadges;
  }

  @HostListener('document:click')
  closeBadgesOnOutsideClick() {
    if (this.showBadges) {
      this.showBadges = false;
    }
  }

  get notificationCount(): number {
    return this.notificationsCount;
  }

  get hasNewNotifications(): boolean {
    return this.notificationCount > 0;
  }

  get isProfileComplete(): boolean {
    return this.profileCompletionPercent >= 100;
  }

  get profileTooltip(): string {
    return this.isProfileComplete
      ? 'Cadastro completo'
      : `Cadastro incompleto (${this.profileCompletionPercent}%)`;
  }

  get pointsTooltip(): string {
    return `Total de pontos: ${this.points}`;
  }

  get notificationsTooltip(): string {
    return this.hasNewNotifications
      ? `${this.notificationCount} notificações novas`
      : 'Sem notificações novas';
  }

  get waterTooltip(): string {
    return `Time Água: ${this.waterAgents} agentes`;
  }

  get fireTooltip(): string {
    return `Time Fogo: ${this.fireAgents} agentes`;
  }

  get occurrencesTooltip(): string {
    return `${this.notificationCount} ocorrências.`;
  }

  goToNotifications(event: Event) {
    event.stopPropagation();
    this.router.navigate(['/mural'], { queryParams: { tab: 'notificacoes' } });
  }

  goToProfile(event: Event) {
    event.stopPropagation();
    this.router.navigate(['/profile']);
  }

  logout(event: Event) {
    event.stopPropagation();
    this.securityService.logout();
    this.router.navigate(['/access/login']);
  }

  isManagementRouteActive(): boolean {
    return this.router.url.startsWith('/management');
  }

  isReportsRouteActive(): boolean {
    return this.router.url.startsWith('/reports');
  }

  goToProfileWithNotifications(event: Event) {
    event.stopPropagation();
    this.router.navigate(['/profile'], {
      queryParams: {
        tab: 'notifications',
        mission: 0,
        occurrence: this.notificationCount,
      },
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : event?.target?.innerWidth;
    this.updateScreenFlags(viewportWidth ?? this.managementFocusMaxWidth);
  }
}
